import './coapplicantResidenceDurationPage.scss';

import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { RadioGroup } from '../../../components/RadioGroup/RadioGroup';
import { RadioOptionClickEvent } from '../../../components/RadioGroup/types/RadioOptionClickEvent';
import { isValidClickEvent } from '../../../components/utils/isValidClickEvent';
import { ONE_YEAR_IN_MONTHS_AS_STRING } from '../../../store/applicantStore/constants/oneYear';
import { TWO_YEARS_IN_MONTHS_AS_STRING } from '../../../store/applicantStore/constants/twoYears';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const {
    computed: { progress },
    next,
    previous,
  } = useWorkflowStore();
  const { setDrawerOpen } = useNavigationPaneStore();

  const [isInitialSelection, setIsInitialSelection] = useState(false);

  const {
    computed: { isCoapplicantPersisted },
    coapplicantCurrentResidenceMonthsAt,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantCurrentResidenceMonthsAt,
    syncCoapplicant,
  } = useCoapplicantStore();

  const { handleSubmit, register, watch } = useForm({
    defaultValues: {
      monthsAtResidenceValue: coapplicantCurrentResidenceMonthsAt,
    },
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const radioOptionClicked = (event: RadioOptionClickEvent<string>): void => {
    if (!('target' in event && 'value' in event.target)) {
      return;
    }

    const monthsAtResidenceValue = event.target.value;

    setIsInitialSelection(
      isEmpty(coapplicantCurrentResidenceMonthsAt) && isValidClickEvent(event),
    );

    setCoapplicantCurrentResidenceMonthsAt(monthsAtResidenceValue);
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { monthsAtResidenceValue } = values;

    if (!monthsAtResidenceValue) {
      return;
    }

    if (isCoapplicantPersisted) {
      const synced = await syncCoapplicant();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  useEffect(() => {
    if (isInitialSelection) {
      handleSubmit(formSubmit)();
    }
  }, [isInitialSelection]);

  return (
    <ThemeProvider theme={theme}>
      <Box
        className="coapplicant-residence-duration-page"
        sx={theme.pageLayout}
      >
        <header
          data-testid={locator.pages.coapplicantResidenceDuration.header.root}
        >
          <button
            aria-label="Previous step"
            data-testid={
              locator.pages.coapplicantResidenceDuration.header
                .previousStepButton
            }
            type="button"
            onClick={previousStep}
          >
            <KeyboardBackspaceRoundedIcon />
          </button>
          <button
            aria-label="Toggle drawer"
            data-testid={
              locator.pages.coapplicantResidenceDuration.header.drawerButton
            }
            type="button"
            onClick={() => {
              setDrawerOpen(true);
            }}
          >
            <MenuRoundedIcon />
          </button>
        </header>

        <div className="progress-container" role="complementary">
          <LinearProgress
            aria-label="Application completed percentage"
            data-testid={
              locator.pages.coapplicantResidenceDuration.progress.root
            }
            value={progress}
            variant="determinate"
          />
        </div>

        <main>
          <form
            data-testid={locator.pages.coapplicantResidenceDuration.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1
                data-testid={locator.pages.coapplicantResidenceDuration.form.h1}
              >
                {t('CoapplicantResidenceDurationPage.title')}
              </h1>
              <h2
                data-testid={locator.pages.coapplicantResidenceDuration.form.h2}
              >
                {t('CoapplicantResidenceDurationPage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantResidenceDuration.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <RadioGroup
                  defaultValue={coapplicantCurrentResidenceMonthsAt || ''}
                  labelPlacement="below"
                  name="monthsAtResidenceValue"
                  options={[
                    {
                      label: t('CoapplicantResidenceDurationPage.yes'),
                      value: TWO_YEARS_IN_MONTHS_AS_STRING,
                      testId:
                        locator.pages.coapplicantResidenceDuration.form
                          .radioOption1,
                    },
                    {
                      label: t('CoapplicantResidenceDurationPage.no'),
                      value: ONE_YEAR_IN_MONTHS_AS_STRING,
                      testId:
                        locator.pages.coapplicantResidenceDuration.form
                          .radioOption2,
                    },
                  ]}
                  register={register}
                  testId={
                    locator.pages.coapplicantResidenceDuration.form
                      .radioGroupRoot
                  }
                  variant="inline"
                  watch={watch}
                  onClick={radioOptionClicked}
                />
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <LoadingButton
                  color="primary"
                  data-testid={
                    locator.pages.coapplicantResidenceDuration.form.submitButton
                  }
                  disabled={isEmpty(coapplicantCurrentResidenceMonthsAt)}
                  loading={isSyncingCoapplicant}
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('CoapplicantResidenceDurationPage.continue')}
                </LoadingButton>
              </div>
            </div>
            <footer
              data-testid={
                locator.pages.coapplicantResidenceDuration.footer.root
              }
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantResidenceDurationPage';
